'use client';

import RoundedImageCard from '@/components/RoundedImageCard';
import Link from 'next/link';
import { FC } from 'react';
import { Document } from '@contentful/rich-text-types';
import MainBodyToReact from '../MainBodyToReact';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { API_ASSET_ROUTE } from '@/config/constants';
import { getLocalizedLink } from '@/utils/getLocalizedLink';
import Icon from '@/components/Icon/Icon';
import { AssetFile } from 'contentful';
import { handleLinkClick } from '@/utils/handleLinkClick';

type TAboutUsCardProps = {
    id?: string;
    image?: string;
    imageDescription: string;
    title: string;
    description?: string | Document;
    link?: string;
    linkText: string;
    icon?: string;
    target?: string;
    file?: AssetFile;
};

const AboutUsCard: FC<TAboutUsCardProps> = ({
    id,
    image,
    imageDescription,
    title,
    description,
    link,
    linkText,
    icon,
    target = '_self',
    file,
}) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: id });
    const { lang } = useSettingsContext();

    const newLink = getLocalizedLink(link, lang);

    return (
        <RoundedImageCard
            image={image}
            alt={imageDescription || title}
            imageHeight={185}
            icon={icon}
            containerClassName="tw-flex tw-flex-col"
            contentClassName="tw-flex tw-flex-col tw-grow tw-items-start tw-p-5"
            inspectorProps={inspectorProps({ fieldId: 'image' })}
        >
            <h2
                className="tw-text-textBlue tw-font-gotham-bold tw-font-light tw-text-[18px] tw-pb-2 tw-leading-[29px]"
                {...inspectorProps({ fieldId: 'title' })}
            >
                {title}
            </h2>
            {description &&
                (typeof description === 'string' ? (
                    <p
                        className="tw-text-textBase tw-font-gotham-book tw-text-[14px] tw-leading-[26px] tw-pb-5"
                        {...inspectorProps({ fieldId: 'content' })}
                    >
                        {description}
                    </p>
                ) : (
                    <MainBodyToReact
                        body={description}
                        classes={{
                            p: 'tw-text-textBase tw-font-gotham-book tw-text-[14px] tw-leading-[26px] tw-pb-5',
                        }}
                        fieldId="content"
                        id={id}
                    />
                ))}
            <Link
                href={file?.fileName ? `${API_ASSET_ROUTE}${file?.fileName}` : newLink ?? '#'}
                target={target}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleLinkClick(e, file?.url)}
                className="tw-mt-auto
                    tw-inline-block
                    tw-tex-[14px]
                    tw-leading-[14px]
                    tw-font-gotham-medium
                    tw-text-pink1
                    tw-border-2
                    tw-border-pink1
                    tw-rounded-md
                    tw-px-4
                    tw-py-3
                    transition-colors
                    tw-duration-300
                    tw-ease-in-out
                    hover:tw-no-underline
                    hover:tw-text-white
                    hover:tw-bg-pink1
                    tw-group"
                {...inspectorProps({ fieldId: 'link' })}
            >
                {linkText}
                <Icon
                    type="angleRight"
                    className="tw-inline tw-ml-1 group-hover:tw-translate-x-1.5 tw-transition-all tw-duration-300 tw-ease-in-out tw-text-pink1 group-hover:tw-fill-white
                    tw-fill-current"
                />
            </Link>
        </RoundedImageCard>
    );
};

export default AboutUsCard;
